import type { AdditionalFieldValues } from "@bbj-types/candidate";
import type { ObjectId, Override } from "@bbj-types/utils";
import type { DifficultyCoachingLevel } from "@inputs/DifficultyToggleGroup";
import { useSuspense } from "@root/api/tools/axios";
import { useCallback } from "react";
import { diagnosisAxiosInstance, useAxiosDiagnosis } from "../../axios";
import type {
  IDiagnosisApplicationFiles,
  IDiagnosisCandidateChallenge,
  IDiagnosisCandidateFields,
  IDiagnosisCandidateObjective,
} from "../../types";

export interface IGetCandidateDraftDiagnosis {
  candidateFields: IDiagnosisCandidateFields;
  candidateDynamicFields: Record<string, AdditionalFieldValues>;
  applicationFiles: IDiagnosisApplicationFiles;
  challenges: Override<
    IDiagnosisCandidateChallenge,
    {
      challenge: IDiagnosisCandidateChallenge["challenge"]["_id"];
      objectives: Override<
        IDiagnosisCandidateObjective,
        { objective: IDiagnosisCandidateObjective["objective"]["_id"] }
      >[];
    }
  >[];
  comment?: string;
  author?: {
    _id: ObjectId;
    firstName: string;
    lastName: string;
  };
  completed: boolean;
  contactChannel: string;
  timeSpent: number;
  updatedAt: Date;
  difficulties?: Record<string, DifficultyCoachingLevel>;
}

export const useGetCandidateDraftDiagnosis = () => {
  const [state, execute, cancel] = useAxiosDiagnosis<IGetCandidateDraftDiagnosis>(
    {
      method: "GET",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: ObjectId) =>
      execute({
        url: `/draft/candidate/${candidateId}`,
      }),
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const useGetCandidateDraftDiagnosisSuspense = (candidateId: ObjectId) =>
  useSuspense(
    () => diagnosisAxiosInstance.get<IGetCandidateDraftDiagnosis>(`/draft/candidate/${candidateId}`),
    [candidateId]
  );
